import { ElementProps } from '@/components/sections/builder/types';
import { getOverrideAnimationParams } from '@/components/sections/builder/settings/animation-setting';
import { ProductDetailTitleElementDataType } from '@/components/sections/builder/elements/body/product-detail/type';
import { Type } from 'lucide-react';
import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/vanilla/shallow';
import { IProduct } from '@/types/product';
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs-custom';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import useMediaQuery from '@/hooks/useMediaQuery';
import { Separator } from '@/components/ui/separator';
import { cn, isEmpty } from '@/lib/utils';
import { ElementDataType } from '@/components/sections/builder/elements/body/product-detail/type';
import InputSelect from '@/components/sections/builder/settings/input-select';

const defaultData: ProductDetailTitleElementDataType = {
  name: 'Description',
  label:
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Quisque at pretium odio. Maecenas viverra tortor consequat, pulvinar felis nec, aliquam diam. Nulla rutrum diam eros, ac dignissim quam pulvinar quis. Aliquam libero metus, posuere vitae maximus ac, suscipit eget mauris. Donec mauris augue, mollis in aliquet sit amet, bibendum vitae sapien.',
};

export const ElementContent = ({ element }: ElementProps) => {
  const [activeTab, setActiveTab] = React.useState('deskripsi');
  const [showMore, setShowMore] = React.useState(false);

  const data = (element.data || defaultData) as ProductDetailTitleElementDataType;
  const parentData = (element.parentData || {}) as IProduct;
  const content = element.parentData as ElementDataType;
  const description =
    parentData.description?.length > 800
      ? parentData.description.slice(0, 800)
      : parentData.description;

  const isMobile = useMediaQuery(768);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  return (
    <>
      {isMobile ? (
        <>
          <Separator className='my-6' />
          <h3 className='text-lg font-bold'>Description</h3>
          <div
            data-animation='true'
            {...getOverrideAnimationParams(data.animation)}
            className={cn(
              'break-words text-base',
              data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
              (data.textStyle === 'subtitle' || !data.textStyle) &&
                'text-sm md:text-base lg:text-lg',
              data.textStyle === 'uppercase' && 'text-xs uppercase md:text-sm lg:text-base'
            )}
            dangerouslySetInnerHTML={{ __html: showMore ? parentData.description : description }}
          />
          {parentData.description?.length > 800 && (
            <button
              className='font-medium text-primary'
              onClick={() => setShowMore(!showMore)}
              style={
                activeTab === 'deskripsi' && !isEmpty(content.colorSchemeProductDetail)
                  ? {
                      color: content?.colorSchemeProductDetail?.textSecondary,
                      borderBottomColor: content?.colorSchemeProductDetail?.textSecondary,
                    }
                  : {}
              }
            >
              {showMore ? 'Show Less' : 'Show More'}
            </button>
          )}
          <Separator className='my-6' />
          <h3 className='text-lg font-bold'>Specification </h3>
          <Table
            className={cn(
              'w-full space-y-2',
              data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
              (data.textStyle === 'subtitle' || !data.textStyle) &&
                'text-sm md:text-base lg:text-lg',
              data.textStyle === 'uppercase' && 'text-xs uppercase md:text-sm lg:text-base'
            )}
          >
            <TableRow className='hover:bg-transparent'>
              <TableCell className='p-1'>Weight</TableCell>
              <TableCell className='p-1 pl-2'>
                {parentData.product_weight
                  ? `${(parentData.product_weight / 1000).toFixed(2)} kg`
                  : '-'}
              </TableCell>
            </TableRow>
            <TableRow className='hover:bg-transparent'>
              <TableCell className='p-1'>Dimension</TableCell>
              <TableCell className='p-1 pl-2'>
                {parentData.product_width} x {parentData.product_height} x{' '}
                {parentData.product_length} cm
              </TableCell>
            </TableRow>
          </Table>
        </>
      ) : (
        <>
          <Tabs defaultValue='deskripsi' className='w-full max-w-2xl'>
            <TabsList className='grid h-auto w-full grid-cols-2 bg-transparent p-0'>
              <TabsTrigger
                value='deskripsi'
                className={cn(
                  'h-auto rounded-none border-b bg-transparent px-4 py-2 font-medium',
                  activeTab === 'deskripsi' &&
                    isEmpty(content.colorSchemeProductDetail) &&
                    'border-b-primary font-bold text-primary'
                )}
                onClick={() => handleTabChange('deskripsi')}
                style={
                  activeTab === 'deskripsi' && !isEmpty(content.colorSchemeProductDetail)
                    ? {
                        color: content?.colorSchemeProductDetail?.textSecondary,
                        borderBottomColor: content?.colorSchemeProductDetail?.textSecondary,
                        fontWeight: 'bold',
                      }
                    : {}
                }
              >
                Description
              </TabsTrigger>
              <TabsTrigger
                value='spesifikasi'
                className={cn(
                  'h-auto rounded-none border-b bg-transparent px-4 py-2 font-medium',
                  activeTab === 'spesifikasi' &&
                    isEmpty(content.colorSchemeProductDetail) &&
                    'border-b-primary font-bold text-primary'
                )}
                style={
                  activeTab === 'spesifikasi' && !isEmpty(content.colorSchemeProductDetail)
                    ? {
                        color: content?.colorSchemeProductDetail?.textSecondary,
                        borderBottomColor: content?.colorSchemeProductDetail?.textSecondary,
                        fontWeight: 'bold',
                      }
                    : {}
                }
                onClick={() => handleTabChange('spesifikasi')}
              >
                Specification
              </TabsTrigger>
            </TabsList>
            <TabsContent value='deskripsi' className='mt-4 space-y-4'>
              <div
                data-animation='true'
                {...getOverrideAnimationParams(data.animation)}
                className={cn(
                  'break-words text-base',
                  data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
                  (data.textStyle === 'subtitle' || !data.textStyle) &&
                    'text-sm md:text-base lg:text-lg',
                  data.textStyle === 'uppercase' && 'text-xs uppercase md:text-sm lg:text-base'
                )}
                dangerouslySetInnerHTML={{
                  __html: showMore ? parentData.description : description,
                }}
              />
              {parentData.description?.length > 800 && (
                <button
                  className='font-medium text-primary'
                  onClick={() => setShowMore(!showMore)}
                  style={
                    activeTab === 'deskripsi' && !isEmpty(content.colorSchemeProductDetail)
                      ? {
                          color: content?.colorSchemeProductDetail?.textSecondary,
                          borderBottomColor: content?.colorSchemeProductDetail?.textSecondary,
                        }
                      : {}
                  }
                >
                  {showMore ? 'Show Less' : 'Show More'}
                </button>
              )}
            </TabsContent>
            <TabsContent value='spesifikasi' className='mt-4'>
              <Table
                className={cn(
                  'w-full space-y-2 border-none text-base',
                  data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
                  (data.textStyle === 'subtitle' || !data.textStyle) &&
                    'text-sm md:text-base lg:text-lg',
                  data.textStyle === 'uppercase' && 'text-xs uppercase md:text-sm lg:text-base'
                )}
              >
                <TableBody>
                  <TableRow className='border-none hover:bg-transparent'>
                    <TableCell className='p-0'>Weight</TableCell>
                    <TableCell className='p-0 pl-4'>
                      {' '}
                      {parentData.product_weight
                        ? `${(parentData.product_weight / 1000).toFixed(2)} kg`
                        : '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow className='border-none hover:bg-transparent'>
                    <TableCell className='p-0'>Dimension</TableCell>
                    <TableCell className='p-0 pl-4'>
                      {parentData.product_width} x {parentData.product_height} x{' '}
                      {parentData.product_length} cm
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </>
      )}
    </>
  );
};

export const ElementPreviewIcon = Type;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
    }),
    shallow
  );

  const data = (element.data || defaultData) as ProductDetailTitleElementDataType;

  return (
    <React.Fragment>
      <div className='my-4 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Text Style</h4>
        <InputSelect
          defaultValue={data.textStyle || 'subtitle'}
          options={[
            { label: 'Body', value: 'body' },
            { label: 'Subtitle', value: 'subtitle' },
            { label: 'UPPERCASE', value: 'uppercase' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, {
              textStyle: v,
            })
          }
        />
      </div>
    </React.Fragment>
  );
}

const ProductDetailDescription = {
  type: 'description',
  group: 'product_detail',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ProductDetailDescription;
